import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import posthog from 'posthog-js'
import { setPensionConfig } from './OnboardingPension.slice'
import BrandLogo from '../../components/Brand/BrandLogo'
import { brand } from '../../config/settings/brand'
import {
  Actions,
  BrandName,
  BrandRow,
  Card,
  DayButton,
  DayGrid,
  Form,
  NoticeWrap,
  Progress,
  ProgressDot,
  ProgressLabel,
  ProgressLine,
  ProgressStep,
  Root,
  SecondaryRow,
  SetupItem,
  SetupList,
  SetupMark,
  SetupProgress,
  SetupProgressBar,
  SetupProgressFill,
  SetupPulse,
  SetupStatus,
  SkipButton,
  Subtitle,
  Title,
  TrustNote,
} from './OnboardingPension.styles'
import { default as Button } from '../../components/Buttons/BaseButton'
import { useInput } from '../../components'
import MessageNotice from '../../components/MessageNotice/MessageNotice'
import PensionConfigForm, {
  validatePaymentDay,
  validatePensionValue,
} from '../PensionConfig/PensionConfigForm'
import { FieldError } from '../PensionConfig/PensionConfigForm.styles'
import routes from '../../config/settings/routes'
import { METHOD } from '../../config/settings/constants'
import { completeOnboarding } from '../UserProfile/UserProfile.slice'
import { fetchLatestImm } from '../../services/onboarding'
import { triggerPensionCalculationForCurrentUser } from '../../services/notifier'
import { hasPensionCalculationForLatestUtm } from '../../services/pension'
import { toUserFacingError } from '../../config/resources/errors'

const STEPS = [
  { key: 'method', label: 'Método' },
  { key: 'amount', label: 'Monto' },
  { key: 'payment', label: 'Fecha de pago' },
  { key: 'setup', label: 'Listo' },
]

const PAYMENT_DAYS = Array.from({ length: 31 }, (_, index) => index + 1)

const SETUP_ITEMS = [
  'Guardando tu configuración',
  'Calculando tu pensión',
  'Listo para ir al panel',
]

const SETUP_PHASE_DWELL_MS = 1800
const CALC_POLL_INTERVAL_MS = 1500
const CALC_POLL_TIMEOUT_MS = 60000

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const ensurePhaseDwell = async (startedAt) => {
  const remaining = SETUP_PHASE_DWELL_MS - (Date.now() - startedAt)
  if (remaining > 0) await wait(remaining)
}

const waitForPensionCalculation = async (userId) => {
  const startedAt = Date.now()
  while (Date.now() - startedAt < CALC_POLL_TIMEOUT_MS) {
    const ready = await hasPensionCalculationForLatestUtm(userId)
    if (ready) return
    await wait(CALC_POLL_INTERVAL_MS)
  }
  throw new Error(
    'El cálculo está tardando más de lo esperado. Inténtalo de nuevo en unos segundos.'
  )
}

const OnboardingPension = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { updateStatus, error: configError } = useSelector(
    (state) => state.adminPensionConfig
  )
  const { status: profileStatus } = useSelector(
    (state) => state.adminUserProfile
  )
  const userId = useSelector((state) => state.adminAuth.user?.id)

  const [step, setStep] = useState(0)
  const [method, setMethod] = useState(METHOD.UTM)
  const [paymentDay, setPaymentDay] = useState(null)
  const [methodError, setMethodError] = useState('')
  const [valueError, setValueError] = useState('')
  const [paymentError, setPaymentError] = useState('')
  const [submitError, setSubmitError] = useState(null)
  const [immValue, setImmValue] = useState(null)
  const [setupPhase, setSetupPhase] = useState(0)
  const [setupReady, setSetupReady] = useState(false)

  const {
    value: amountValue,
    setValue: setAmountValue,
    onChange: handleAmountChange,
  } = useInput({ initialValue: '' })

  const {
    value: percentageValue,
    setValue: setPercentageValue,
    onChange: handlePercentageChange,
  } = useInput({ initialValue: '' })

  const isSaving =
    updateStatus === 'loading' || profileStatus?.update === 'loading'
  const isSetupStep = step === 3

  useEffect(() => {
    let cancelled = false
    fetchLatestImm()
      .then((imm) => {
        if (!cancelled) setImmValue(imm?.value ?? null)
      })
      .catch(() => {
        if (!cancelled) setImmValue(null)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleSkip = () => {
    posthog.capture('onboarding_skipped')
    sessionStorage.setItem('onboarding_skipped', 'true')
    navigate(routes.dashboard, { replace: true })
  }

  const handleMethodChange = (nextMethod) => {
    setMethod(nextMethod)
    setMethodError('')
    setValueError('')
    setAmountValue('')
    setPercentageValue('')
  }

  const goNextFromMethod = () => {
    if (!method) {
      setMethodError('Elige un tipo de reajuste.')
      return
    }
    setMethodError('')
    posthog.capture('onboarding_step_completed', { step: 'method', method })
    setStep(1)
  }

  const goNextFromAmount = () => {
    const validation = validatePensionValue(
      method,
      percentageValue,
      amountValue
    )
    if (!validation.valid) {
      setValueError(validation.error)
      return
    }
    setValueError('')
    posthog.capture('onboarding_step_completed', { step: 'amount', method })
    setStep(2)
  }

  const finishOnboarding = async () => {
    const paymentValidation = validatePaymentDay(paymentDay)
    if (!paymentValidation.valid) {
      setPaymentError(paymentValidation.error)
      return
    }
    setPaymentError('')
    setSubmitError(null)

    const valueValidation = validatePensionValue(
      method,
      percentageValue,
      amountValue
    )
    if (!valueValidation.valid) {
      setValueError(valueValidation.error)
      setStep(1)
      return
    }

    setStep(3)
    setSetupPhase(0)
    setSetupReady(false)

    try {
      sessionStorage.setItem('onboarding_skipped', 'false')

      const phase0StartedAt = Date.now()
      await dispatch(
        setPensionConfig({
          method,
          amount: method === METHOD.UTM ? amountValue : '',
          percentage: method === METHOD.IMM ? percentageValue : '',
          paymentDay,
        })
      ).unwrap()
      await ensurePhaseDwell(phase0StartedAt)

      setSetupPhase(1)

      if (!userId) {
        throw new Error('Sesión no disponible. Vuelve a iniciar sesión.')
      }

      const phase1StartedAt = Date.now()
      try {
        await triggerPensionCalculationForCurrentUser()
        await waitForPensionCalculation(userId)
      } catch {
        // Config already saved — don't block onboarding if the calc API is down.
      }
      await ensurePhaseDwell(phase1StartedAt)

      setSetupPhase(2)

      const phase2StartedAt = Date.now()
      await dispatch(completeOnboarding()).unwrap()
      await ensurePhaseDwell(phase2StartedAt)

      setSetupReady(true)
      posthog.capture('onboarding_completed', {
        method,
        payment_day: paymentDay,
      })
    } catch (error) {
      setStep(2)
      setSetupPhase(0)
      setSetupReady(false)
      setSubmitError(
        toUserFacingError(
          typeof error === 'string' ? error : error,
          'No pudimos guardar tu configuración. Inténtalo de nuevo.'
        )
      )
    }
  }

  const goToDashboard = () => {
    navigate(routes.dashboard, { replace: true })
  }

  const handleBack = () => {
    if (step <= 0 || isSetupStep) return
    setSubmitError(null)
    setStep((current) => current - 1)
  }

  const handlePrimary = (event) => {
    event.preventDefault()
    if (step === 0) goNextFromMethod()
    else if (step === 1) goNextFromAmount()
    else if (step === 2) finishOnboarding()
  }

  const notice = submitError || configError

  const stepTitle = {
    0: '¿Cómo se reajusta tu pensión?',
    1:
      method === METHOD.IMM
        ? '¿Qué porcentaje del sueldo mínimo?'
        : '¿Cuántas UTM te corresponden?',
    2: '¿Qué día de pago indica tu resolución?',
    3: setupReady ? 'Todo listo' : 'Preparando tu espacio',
  }[step]

  const stepSubtitle = {
    0: 'Puedes elegir el estándar actual en UTM o el reajuste antiguo por sueldo mínimo.',
    1:
      method === METHOD.IMM
        ? 'Típico: 40% por un hijo, o 30% por cada uno si son más.'
        : 'Por ejemplo, 2,05 si así lo indica tu resolución.',
    2: 'Usaremos este día para tus recordatorios. Puedes cambiarlo después.',
    3: setupReady
      ? 'Tu pensión ya está calculada. Entra al panel cuando quieras.'
      : setupPhase === 1
        ? 'Estamos calculando tu monto estimado. No cierres esta ventana.'
        : 'Estamos guardando tu configuración. No cierres esta ventana.',
  }[step]

  return (
    <Root>
      <Card>
        <BrandRow>
          <BrandLogo size={36} />
          <BrandName>{brand.name}</BrandName>
        </BrandRow>

        <Progress aria-hidden="true">
          {STEPS.map((item, index) => (
            <ProgressStep key={item.key}>
              <ProgressDot
                $active={index === step}
                $done={index < step}
              />
              {index < STEPS.length - 1 && (
                <ProgressLine $done={index < step} />
              )}
            </ProgressStep>
          ))}
        </Progress>
        <ProgressLabel>
          Paso {Math.min(step + 1, STEPS.length)} de {STEPS.length} ·{' '}
          {STEPS[step].label}
        </ProgressLabel>

        <Title>{stepTitle}</Title>
        <Subtitle>{stepSubtitle}</Subtitle>
        {step === 0 && (
          <TrustNote>
            Solo el factor de tu resolución (UTM o % del sueldo mínimo) y el
            día de pago. No subimos la sentencia ni pedimos RUT o datos
            bancarios.
          </TrustNote>
        )}

        {notice && !isSetupStep && (
          <NoticeWrap>
            <MessageNotice type="error">{notice}</MessageNotice>
          </NoticeWrap>
        )}

        {isSetupStep ? (
          <>
            <SetupProgress
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={SETUP_ITEMS.length}
              aria-valuenow={
                setupReady ? SETUP_ITEMS.length : Math.min(setupPhase + 1, SETUP_ITEMS.length)
              }
              aria-label="Progreso de la preparación"
            >
              <SetupProgressBar>
                <SetupProgressFill
                  $progress={
                    setupReady
                      ? 1
                      : (setupPhase + 0.45) / SETUP_ITEMS.length
                  }
                />
              </SetupProgressBar>
            </SetupProgress>

            <SetupList aria-live="polite">
              {SETUP_ITEMS.map((label, index) => {
                const done =
                  setupPhase > index ||
                  (setupReady && index === SETUP_ITEMS.length - 1)
                const active = setupPhase === index && !setupReady
                return (
                  <SetupItem
                    key={label}
                    $done={done}
                    $active={active}
                    style={{ animationDelay: `${index * 80}ms` }}
                  >
                    <SetupMark $done={done} $active={active}>
                      {done ? '✓' : active ? <SetupPulse /> : index + 1}
                    </SetupMark>
                    <span>{label}</span>
                  </SetupItem>
                )
              })}
            </SetupList>

            <SetupStatus $ready={setupReady}>
              {setupReady
                ? 'Listo. Ya puedes entrar a tu panel.'
                : setupPhase === 0
                  ? 'Guardando tus datos…'
                  : setupPhase === 1
                    ? 'Calculando tu monto estimado…'
                    : 'Cerrando la configuración…'}
            </SetupStatus>

            {setupReady && (
              <Actions>
                <Button type="button" width="100%" onClick={goToDashboard}>
                  Ir al panel
                </Button>
              </Actions>
            )}
          </>
        ) : (
          <Form onSubmit={handlePrimary}>
            {step === 0 && (
              <PensionConfigForm
                method={method}
                amount={amountValue}
                percentage={percentageValue}
                onMethodChange={handleMethodChange}
                onAmountChange={handleAmountChange}
                onPercentageChange={handlePercentageChange}
                methodError={methodError}
                showMethod
                showMethodLabel={false}
                showValue={false}
                disabled={isSaving}
              />
            )}

            {step === 1 && (
              <PensionConfigForm
                method={method}
                amount={amountValue}
                percentage={percentageValue}
                onMethodChange={handleMethodChange}
                onAmountChange={handleAmountChange}
                onPercentageChange={handlePercentageChange}
                valueError={valueError}
                showMethod={false}
                showValue
                showValueLabel={false}
                showValueHint={false}
                valueInputFullWidth
                immValue={immValue}
                disabled={isSaving}
              />
            )}

            {step === 2 && (
              <>
                <DayGrid role="listbox" aria-label="Día de pago según tu resolución">
                  {PAYMENT_DAYS.map((day) => (
                    <DayButton
                      key={day}
                      type="button"
                      role="option"
                      aria-selected={paymentDay === day}
                      $active={paymentDay === day}
                      disabled={isSaving}
                      onClick={() => {
                        setPaymentDay(day)
                        setPaymentError('')
                      }}
                    >
                      {day}
                    </DayButton>
                  ))}
                </DayGrid>
                {paymentError && (
                  <FieldError $centered>{paymentError}</FieldError>
                )}
              </>
            )}

            <Actions>
              {step === 0 ? (
                <Button type="submit" width="100%">
                  Continuar
                </Button>
              ) : (
                <SecondaryRow>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleBack}
                    disabled={isSaving}
                  >
                    Atrás
                  </Button>
                  <Button type="submit" loading={isSaving} width="100%">
                    {step === 2 ? 'Finalizar' : 'Continuar'}
                  </Button>
                </SecondaryRow>
              )}
              <SkipButton
                type="button"
                onClick={handleSkip}
                disabled={isSaving}
              >
                Saltar por ahora
              </SkipButton>
            </Actions>
          </Form>
        )}
      </Card>
    </Root>
  )
}

export default OnboardingPension
